import { createSelectorFunctions } from 'auto-zustand-selectors-hook'
import { create } from 'zustand'

import type { TCookieConsentByCategory } from 'types/cookie-consent/cookie-consent.types'

/**
 * TCS-8180: cookie-consent store (LGPD/ANPD).
 *
 * Holds the granular consent state per category key (the keys are dynamic,
 * defined by the CMS cards' gtmName) plus the UI state for the banner and the
 * preferences center. Persistence (localStorage + consent API) lives in the
 * hook/component layer, not here, so the store stays serializable and
 * testable.
 */
export interface CookieConsentStore {
  /** Whether the user has saved a consent decision (any version) */
  hasConsent: boolean
  /** Consent per category key (gtmName); essential categories are always true */
  consents: TCookieConsentByCategory
  /** Whether the banner is currently visible */
  isBannerVisible: boolean
  /** Whether the preferences center modal is currently open */
  isPreferencesOpen: boolean
  /**
   * Whether the preferences center has been opened at least once this session.
   * Once it opens, the banner is dismissed for the session: after the modal
   * closes, only the floating button remains as entry point.
   */
  hasOpenedPreferences: boolean

  /**
   * Hydrate the store from a persisted consent (UI format: booleans per
   * category key). Called on mount by the banner component after reading
   * localStorage (and, later, the consent API).
   */
  hydrateConsent: (consents: TCookieConsentByCategory) => void

  /**
   * Invalidate the current consent (e.g. the CMS cookie policy version no
   * longer matches the stored record): resets the consent state so the banner
   * is shown again.
   */
  clearConsent: () => void

  /** Persist an "accept all" choice over the given category keys */
  acceptAll: (categoryKeys: string[]) => void

  /**
   * Persist a "refuse non-essential" choice: essential keys stay on, every
   * other category key is explicitly denied (so reopening the preferences
   * center shows the saved state instead of the CMS default).
   */
  refuseNonEssential: (categoryKeys: string[], essentialKeys: string[]) => void

  /** Persist a granular choice from the preferences center */
  saveConsents: (consents: TCookieConsentByCategory) => void

  /** Show the banner again (e.g. "review cookies" entry point) */
  showBanner: () => void

  openPreferences: () => void
  closePreferences: () => void
}

export const useCookieConsentStore = createSelectorFunctions(
  create<CookieConsentStore>((set) => ({
    hasConsent: false,
    consents: {},
    isBannerVisible: false,
    isPreferencesOpen: false,
    hasOpenedPreferences: false,

    hydrateConsent: (consents) =>
      set({
        hasConsent: true,
        consents: { ...consents },
        // A valid stored consent means the banner must not be shown again
        isBannerVisible: false
      }),

    clearConsent: () =>
      set({
        hasConsent: false,
        consents: {},
        isBannerVisible: false
      }),

    acceptAll: (categoryKeys) =>
      set({
        hasConsent: true,
        consents: Object.fromEntries(
          categoryKeys.map((key) => [key, true])
        ) as TCookieConsentByCategory,
        isBannerVisible: false,
        isPreferencesOpen: false
      }),

    refuseNonEssential: (categoryKeys, essentialKeys) =>
      set({
        hasConsent: true,
        consents: {
          // Essential cards are always granted; everything else is denied
          ...Object.fromEntries(
            categoryKeys.map((key) => [key, essentialKeys.includes(key)])
          )
        } as TCookieConsentByCategory,
        isBannerVisible: false,
        isPreferencesOpen: false
      }),

    saveConsents: (consents) =>
      set({
        hasConsent: true,
        consents: { ...consents },
        isBannerVisible: false,
        isPreferencesOpen: false
      }),

    showBanner: () => set({ isBannerVisible: true }),

    /**
     * Opening the preferences center dismisses the banner for the session:
     * the modal becomes the only visible surface. When it closes, only the
     * floating button remains as entry point (the banner never comes back —
     * the user is already in the configuration flow).
     */
    openPreferences: () =>
      set({
        isPreferencesOpen: true,
        isBannerVisible: false,
        hasOpenedPreferences: true
      }),

    closePreferences: () => set({ isPreferencesOpen: false })
  }))
)
